// src/pages/analytics/payables/VendorPriceChangesTab.tsx
//
// Vendor price changes (recipe vendor-price-changes, agent 3.24.0+): the unit
// cost each vendor billed per item at the end of the range against the cost
// going into it, the cost impact on the range's quantity, and the sell price
// over the same time — "Squeeze" = cost went up more than the sell price.
// Click an item for its full cost history across vendors.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Search, Tags } from 'lucide-react';
import { apiV2 as api } from '../../../services/apiV2';
import DateField from '../../../components/DateField';
import Modal from '../../../components/Modal';
import type { VendorPriceRow } from '../../../types/payables';
import { exportCsv, type CsvColumns } from '../../../lib/csvExport';
import { exportXlsx } from '../../../lib/xlsxExport';
import {
  ChangePct, ExportButtons, LoadError, Spinner, Tile, controlsCls, fmtCost, fmtCur, fmtNum,
  isoDaysAgo, labelCls, pill, tableWrap, tdCls, tdR, thCls, thR,
} from './apShared';

const SHOW = [
  { value: 'changed', label: 'Changed' },
  { value: 'up', label: 'Increases' },
  { value: 'down', label: 'Decreases' },
  { value: 'squeeze', label: 'Margin squeeze' },
  { value: 'all', label: 'All' },
];

const COLS: CsvColumns<VendorPriceRow> = [
  ['Vendor code', r => r.code], ['Vendor', r => r.name], ['Item', r => r.item], ['Description', r => r.desc],
  ['Category', r => r.category], ['Starting cost', r => r.baseCost], ['Last cost', r => r.lastCost],
  ['Change %', r => r.changePct], ['Changes', r => r.changes], ['Last change', r => r.lastChange ?? ''],
  ['Min cost', r => r.minCost], ['Max cost', r => r.maxCost], ['Qty', r => r.qty], ['Spend', r => r.spend],
  ['Cost impact', r => r.impact], ['Sell before', r => r.sellBefore ?? ''], ['Sell now', r => r.sellNow ?? ''],
  ['Sell change %', r => r.sellChangePct ?? ''], ['GP % before', r => r.gpPctBefore ?? ''], ['GP % now', r => r.gpPctNow ?? ''],
  ['Squeeze', r => r.squeeze ? 'Yes' : ''],
];

export function VendorPriceChangesTab() {
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(180));
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const [show, setShow] = useState('changed');
  const [minPct, setMinPct] = useState(1);
  const [search, setSearch] = useState('');
  const [history, setHistory] = useState<VendorPriceRow | null>(null);

  const q = useQuery({
    queryKey: ['vendorPriceChanges', dateFrom, dateTo],
    queryFn: () => api.getVendorPriceChanges({ dateFrom, dateTo }),
    staleTime: 60_000,
    retry: 0,
  });
  const data = q.data;

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (data?.rows ?? []).filter(r => {
      if (s && ![r.item, r.desc, r.name, r.category, String(r.code)].some(x => x.toLowerCase().includes(s))) return false;
      switch (show) {
        case 'changed': return Math.abs(r.changePct) >= minPct;
        case 'up': return r.changePct >= minPct;
        case 'down': return r.changePct <= -minPct;
        case 'squeeze': return r.squeeze;
        default: return true;
      }
    });
  }, [data, search, show, minPct]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><Tags size={20} /> Vendor Price Changes</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Billed unit cost per vendor and item, start of range vs latest, and whether the sell price kept up
          {data && <span className="text-slate-500"> · {data.from} → {data.to}</span>}
        </p>
      </div>

      <div className={controlsCls}>
        <DateField label="From" value={dateFrom} onChange={setDateFrom} />
        <DateField label="To" value={dateTo} onChange={setDateTo} />
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Show</span>
          <div className="flex gap-1 flex-wrap">
            {SHOW.map(s => <button key={s.value} className={pill(show === s.value)} onClick={() => setShow(s.value)}>{s.label}</button>)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Min change %</span>
          <input type="number" min={0} step={0.5} value={minPct}
            onChange={e => { const v = Number(e.target.value); if (Number.isFinite(v)) setMinPct(Math.max(0, v)); }}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-20 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Search</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Item, vendor, category…"
              className="pl-6 pr-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-52 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <div className="flex-1" />
        <ExportButtons what="price changes" disabled={!rows.length}
          onCsv={() => exportCsv('vendor-price-changes', COLS, rows)} onXlsx={() => exportXlsx('vendor-price-changes', COLS, rows)} />
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && <LoadError error={q.error} what="vendor price changes" />}

      {q.isLoading ? <Spinner /> : !q.error && data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Tile tone="violet" label="Items bought" value={data.totals.items} sub={`${fmtCur(data.totals.spend)} spend`} />
            <Tile tone="red" label="Cost increases" value={data.totals.increased} />
            <Tile tone="emerald" label="Cost decreases" value={data.totals.decreased} />
            <Tile tone={data.totals.impact > 0 ? 'red' : 'emerald'} label="Cost impact" value={fmtCur(data.totals.impact)}
              sub="vs buying at the starting cost" />
            <Tile tone="amber" label="Margin squeeze" value={data.totals.squeezed} sub="cost up more than sell price" />
          </div>

          <div className={`${tableWrap} max-h-[620px]`}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className={thCls}>Item</th><th className={thCls}>Vendor</th>
                  <th className={thR}>Start cost</th><th className={thR}>Last cost</th><th className={thR}>Change</th>
                  <th className={thR}>Changes</th><th className={thCls}>Last change</th>
                  <th className={thR}>Qty</th><th className={thR}>Impact</th>
                  <th className={thR}>Sell then → now</th><th className={thR}>Sell change</th><th className={thR}>GP % then → now</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 2000).map(r => (
                  <tr key={`${r.code}-${r.item}`} onClick={() => setHistory(r)}
                    className={`border-t border-slate-800/60 cursor-pointer hover:bg-slate-800/40 ${r.squeeze ? 'bg-amber-500/5' : ''}`}>
                    <td className={tdCls}>{r.item} <span className="text-slate-500">{r.desc}</span></td>
                    <td className={tdCls}>{r.name}</td>
                    <td className={tdR}>{fmtCost(r.baseCost)}</td>
                    <td className={`${tdR} font-medium`}>{fmtCost(r.lastCost)}</td>
                    <td className={tdR}><ChangePct pct={r.changePct} invert /></td>
                    <td className={tdR}>{r.changes || '–'}</td>
                    <td className={tdCls}>{r.lastChange ?? '–'}</td>
                    <td className={tdR}>{fmtNum(r.qty)}</td>
                    <td className={`${tdR} ${r.impact > 0.5 ? 'text-red-300' : r.impact < -0.5 ? 'text-emerald-300' : 'text-slate-500'}`}>{fmtCur(r.impact)}</td>
                    <td className={tdR}>{r.sellBefore != null ? fmtCost(r.sellBefore) : '–'} → {r.sellNow != null ? fmtCost(r.sellNow) : '–'}</td>
                    <td className={tdR}><ChangePct pct={r.sellChangePct} /></td>
                    <td className={tdR}>
                      {r.gpPctBefore != null ? `${r.gpPctBefore.toFixed(1)}%` : '–'} → {' '}
                      <span className={r.gpPctNow != null && r.gpPctBefore != null && r.gpPctNow < r.gpPctBefore - 0.5 ? 'text-red-300' : ''}>
                        {r.gpPctNow != null ? `${r.gpPctNow.toFixed(1)}%` : '–'}
                      </span>
                      {r.squeeze && <span className="ml-1.5 px-1 rounded bg-amber-500/20 text-amber-300 text-[10px]">squeeze</span>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-500">Nothing matches.</td></tr>}
              </tbody>
            </table>
          </div>
          {rows.length > 2000 && <p className="text-[11px] text-slate-500">Showing the first 2,000 of {rows.length} — export for all.</p>}
          <p className="text-[11px] text-slate-500">
            Costs are per unit as billed and sell prices per unit as invoiced; an item bought and sold in different units reads off, so treat GP % as a guide.
          </p>
        </>
      )}

      {history && <HistoryModal row={history} dateFrom={dateFrom} dateTo={dateTo} onClose={() => setHistory(null)} />}
    </div>
  );
}

function HistoryModal({ row, dateFrom, dateTo, onClose }: { row: VendorPriceRow; dateFrom: string; dateTo: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['vendorPriceHistory', row.item, dateFrom, dateTo],
    queryFn: () => api.getVendorPriceHistory(row.item, { dateFrom, dateTo }),
    staleTime: 60_000,
  });
  return (
    <Modal title={`Cost history — ${row.item} ${row.desc}`} onClose={onClose} panelClassName="max-w-3xl">
      {q.isLoading ? <Spinner /> : q.error ? <LoadError error={q.error} what="the cost history" /> : (
        <div className="space-y-2 text-xs">
          <p className="text-slate-400">All vendors, {q.data?.from} → {q.data?.to} (includes the year before the range).</p>
          <div className={`${tableWrap} max-h-[460px]`}>
            <table className="w-full">
              <thead className="sticky top-0 bg-slate-900">
                <tr><th className={thCls}>Date</th><th className={thCls}>Vendor</th><th className={thCls}>Bill</th><th className={thR}>Qty</th><th className={thR}>Cost</th><th className={thR}>vs previous</th></tr>
              </thead>
              <tbody>
                {(q.data?.history ?? []).map((h, i, arr) => {
                  const prev = i > 0 ? arr[i - 1].cost : null;
                  const pct = prev && prev > 0 ? (h.cost - prev) / prev * 100 : null;
                  return (
                    <tr key={`${h.doc}-${i}`} className="border-t border-slate-800/60">
                      <td className={tdCls}>{h.date}</td>
                      <td className={tdCls}>{h.name}</td>
                      <td className={tdCls}>{h.doc}</td>
                      <td className={tdR}>{fmtNum(h.qty)}</td>
                      <td className={tdR}>{fmtCost(h.cost)}</td>
                      <td className={tdR}>{pct != null && Math.abs(pct) >= 0.05 ? <ChangePct pct={pct} invert /> : <span className="text-slate-600">–</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
